
import React from 'react';
import { SimulationConfig, BallFillType, BallTexture } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { ControlGroup } from './ControlGroup';

interface SphereDisplaySettingsProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const SphereDisplaySettings: React.FC<SphereDisplaySettingsProps> = ({ config, setConfig }) => {
  const fillTypes: BallFillType[] = ['SOLID', 'GRADIENT_RADIAL', 'GRADIENT_LINEAR', 'HOLLOW'];
  const textures: BallTexture[] = ['NONE', 'STRIPES', 'DOTS', 'CROSSHATCH', 'NOISE'];

  const ColorPicker = ({ label, value, onChange, settingKey }: { label: string, value: string, onChange: (v: string) => void, settingKey?: string }) => (
    <ControlGroup label={label} settingKey={settingKey} darkMode={config.darkMode}>
      <div className={`flex items-center gap-3 p-1.5 rounded-2xl border ${config.darkMode ? 'bg-black/40 border-white/5' : 'bg-white border-neutral-200'} shadow-inner`}>
        <div className="relative group">
          <div className="w-10 h-10 rounded-full border-2 border-white/10 shadow-lg cursor-pointer transition-transform group-hover:scale-105 active:scale-95" style={{ backgroundColor: value }} />
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
        </div>
        <input 
          type="text" 
          value={value} 
          onChange={e => onChange(e.target.value)} 
          className={`flex-1 px-3 py-2 text-[11px] font-mono font-bold border rounded-xl outline-none transition-all ${
            config.darkMode 
            ? 'bg-black/20 border-white/5 text-white focus:border-white/20' 
            : 'bg-neutral-50 border-neutral-100 text-neutral-800 focus:border-neutral-300'
          }`} 
        />
      </div>
    </ControlGroup>
  );

  return (
    <div className="flex flex-col gap-5">
      {/* GEOMETRY & PHYSICS VIS */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-100'} flex flex-col gap-4 shadow-sm`}>
         <span className="text-[11px] font-black uppercase text-blue-500 opacity-80 mb-2 block">Geometry & Physics FX</span>
         <SmartInput label="Radius" min={1} max={150} value={config.ballRadius} onChange={v => setConfig(p => ({...p, ballRadius: v}))} darkMode={config.darkMode} settingKey="ballRadius" />
         
         <div className="flex flex-col gap-4">
             <ControlGroup label="Squash & Stretch" settingKey="ballSquashStretch" darkMode={config.darkMode}>
                <div className={`flex items-center justify-end p-2 rounded-2xl ${config.darkMode ? 'bg-blue-500/5 border border-blue-500/10' : 'bg-white border border-blue-100'}`}>
                    <Toggle active={config.ballSquashStretch} onClick={() => setConfig(p => ({...p, ballSquashStretch: !p.ballSquashStretch}))} color="bg-blue-500" />
                </div>
             </ControlGroup>
             <SmartInput label="Squash Intensity" min={0.1} max={2.0} step={0.1} value={config.ballSquashFactor} onChange={v => setConfig(p => ({...p, ballSquashFactor: v}))} darkMode={config.darkMode} settingKey="ballSquashFactor" />
         </div>
         <SmartInput label="Pulse Speed" min={0} max={20} value={config.ballPulseSpeed} onChange={v => setConfig(p => ({...p, ballPulseSpeed: v}))} darkMode={config.darkMode} settingKey="ballPulseSpeed" />
      </div>

      {/* FILL & SURFACE */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-purple-500/5 border-purple-500/10' : 'bg-purple-50 border-purple-100'} flex flex-col gap-4 shadow-sm`}>
         <span className="text-[11px] font-black uppercase text-purple-500 opacity-80 mb-2 block">Surface Material</span>
         
         <ControlGroup label="Fill Mode" settingKey="ballFillType" darkMode={config.darkMode}>
            <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                <select value={config.ballFillType} onChange={e => setConfig(p => ({...p, ballFillType: e.target.value as BallFillType}))} className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}>
                    {fillTypes.map(t => <option key={t} value={t} className="bg-neutral-900 text-white">{t.replace(/_/g, ' ')}</option>)}
                </select>
            </div>
         </ControlGroup>

         <div className="flex flex-col gap-4">
             <ColorPicker label="Base Color" value={config.ballColor} onChange={v => setConfig(p => ({...p, ballColor: v}))} settingKey="ballColor" />
             <SmartInput label="Opacity" min={0} max={1} step={0.05} value={config.ballFillOpacity} onChange={v => setConfig(p => ({...p, ballFillOpacity: v}))} darkMode={config.darkMode} settingKey="ballFillOpacity" />
         </div>

         {(config.ballFillType === 'GRADIENT_RADIAL' || config.ballFillType === 'GRADIENT_LINEAR') && (
             <div className={`grid grid-cols-1 gap-4 p-4 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-neutral-50 border-neutral-100 shadow-inner'}`}>
                 <ColorPicker label="Gradient Inner" value={config.ballGradientInner} onChange={v => setConfig(p => ({...p, ballGradientInner: v}))} settingKey="ballGradientInner" />
                 <ColorPicker label="Gradient Outer" value={config.ballGradientOuter} onChange={v => setConfig(p => ({...p, ballGradientOuter: v}))} settingKey="ballGradientOuter" />
             </div>
         )}

         <ControlGroup label="Texture Overlay" settingKey="ballTexture" darkMode={config.darkMode}>
            <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                <select value={config.ballTexture} onChange={e => setConfig(p => ({...p, ballTexture: e.target.value as BallTexture}))} className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}>
                    {textures.map(t => <option key={t} value={t} className="bg-neutral-900 text-white">{t}</option>)}
                </select>
            </div>
         </ControlGroup>
         {config.ballTexture !== 'NONE' && (
             <SmartInput label="Texture Opacity" min={0} max={1} step={0.05} value={config.ballTextureOpacity} onChange={v => setConfig(p => ({...p, ballTextureOpacity: v}))} darkMode={config.darkMode} settingKey="ballTextureOpacity" />
         )}
      </div>

      {/* BORDER & OUTLINE */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50 border-emerald-100'} flex flex-col gap-4 shadow-sm`}>
         <span className="text-[11px] font-black uppercase text-emerald-500 opacity-80 mb-2 block">Border Engine</span>
         <div className="flex flex-col gap-4">
             <SmartInput label="Width" min={0} max={20} value={config.ballStrokeWidth} onChange={v => setConfig(p => ({...p, ballStrokeWidth: v}))} darkMode={config.darkMode} settingKey="ballStrokeWidth" />
             <SmartInput label="Opacity" min={0} max={1} step={0.05} value={config.ballStrokeOpacity} onChange={v => setConfig(p => ({...p, ballStrokeOpacity: v}))} darkMode={config.darkMode} settingKey="ballStrokeOpacity" />
         </div>
         <ColorPicker label="Border Color" value={config.ballStrokeColor} onChange={v => setConfig(p => ({...p, ballStrokeColor: v}))} settingKey="ballStrokeColor" />
      </div>

      {/* GLOW & FX */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-orange-500/5 border-orange-500/10' : 'bg-orange-50 border-orange-100'} flex flex-col gap-4 shadow-sm`}>
         <span className="text-[11px] font-black uppercase text-orange-500 opacity-80 mb-2 block">Glow & Radiance</span>
         <div className="flex flex-col gap-4">
             <SmartInput label="Glow Radius" min={0} max={100} value={config.ballGlowRadius} onChange={v => setConfig(p => ({...p, ballGlowRadius: v}))} darkMode={config.darkMode} settingKey="ballGlowRadius" />
             <SmartInput label="Intensity" min={0} max={1} step={0.05} value={config.ballGlowOpacity} onChange={v => setConfig(p => ({...p, ballGlowOpacity: v}))} darkMode={config.darkMode} settingKey="ballGlowOpacity" />
         </div>
         <ColorPicker label="Glow Color" value={config.ballGlowColor} onChange={v => setConfig(p => ({...p, ballGlowColor: v}))} settingKey="ballGlowColor" />
      </div>
      
      {/* MULTI-BALL COLORS (Legacy/Compat support) */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-neutral-500/5 border-neutral-500/10' : 'bg-neutral-50 border-neutral-100'} flex flex-col gap-4 shadow-sm`}>
        <span className="text-[11px] font-black uppercase opacity-40 mb-2 block">Multi-Ball Overrides</span>
        <div className="grid grid-cols-2 gap-4">
            <ColorPicker label="Sphere A" value={config.ball1Color} onChange={v => setConfig(p => ({...p, ball1Color: v}))} />
            <ColorPicker label="Sphere B" value={config.ball2Color} onChange={v => setConfig(p => ({...p, ball2Color: v}))} />
        </div>
      </div>
    </div>
  );
};
